import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * Site-styled replacement for `window.confirm`.
 *
 * The native dialog can't be styled, looks like a browser security prompt, and
 * (being synchronous) blocks the whole page. This is promise-based so calling
 * code reads the same way:
 *
 *   if (!(await confirm({ title: '…', message: '…' }))) return;
 */

export type ConfirmOptions = {
	title: string;
	message: React.ReactNode;
	confirmLabel?: string;
	cancelLabel?: string;
	/** Red confirm button — use for destructive actions. */
	danger?: boolean;
};

type PendingConfirm = ConfirmOptions & { resolve: (ok: boolean) => void };

export function useConfirm() {
	const [pending, setPending] = useState<PendingConfirm | null>(null);
	const confirmBtnRef = useRef<HTMLButtonElement>(null);

	const confirm = useCallback(
		(options: ConfirmOptions) =>
			new Promise<boolean>((resolve) => {
				setPending({ ...options, resolve });
			}),
		[],
	);

	const settle = useCallback(
		(ok: boolean) => {
			setPending((p) => {
				p?.resolve(ok);
				return null;
			});
		},
		[],
	);

	// Esc cancels, Enter confirms — same reflexes the native dialog trained.
	useEffect(() => {
		if (!pending) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				e.preventDefault();
				settle(false);
			} else if (e.key === 'Enter') {
				e.preventDefault();
				settle(true);
			}
		};
		window.addEventListener('keydown', onKey);
		confirmBtnRef.current?.focus();
		return () => window.removeEventListener('keydown', onKey);
	}, [pending, settle]);

	const dialog = pending ? (
		<div
			className='fixed inset-0 z-[10050] bg-black/40 flex items-center justify-center p-4'
			onClick={() => settle(false)}
			role='dialog'
			aria-modal='true'
		>
			<div
				className='bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-sm p-6'
				onClick={(e) => e.stopPropagation()}
			>
				<div className='flex items-start gap-3 mb-4'>
					<div
						className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${pending.danger ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'}`}
					>
						<AlertTriangle className='w-5 h-5' />
					</div>
					<div className='min-w-0'>
						<h2 className='text-lg font-extrabold text-gray-900 m-0 text-left'>{pending.title}</h2>
					</div>
				</div>

				<div className='text-sm text-gray-600 leading-relaxed text-left mb-6'>{pending.message}</div>

				<div className='flex items-center justify-end gap-2'>
					<button
						onClick={() => settle(false)}
						className='rounded-xl border border-gray-200 px-4 h-9 font-bold text-gray-700 hover:bg-gray-50'
					>
						{pending.cancelLabel || 'Cancel'}
					</button>
					<button
						ref={confirmBtnRef}
						onClick={() => settle(true)}
						className={`rounded-xl px-4 h-9 font-bold text-white ${pending.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
					>
						{pending.confirmLabel || 'Confirm'}
					</button>
				</div>
			</div>
		</div>
	) : null;

	return { confirm, confirmDialog: dialog };
}
